import type {Architecture, Platform, PluginFormat} from "../../api/models";

// The product catalog, product pages and downloads. Product names, descriptions, editions and release notes come from
// the API, as entered in the administration area.
export const products = {
    closedBeta: 'Early Access',
    noSuchProduct: 'There is no such product.',
    download: 'Download',

    catalog: {
        title: 'Products',
        description: "Tristar Advanced Software's audio plug-ins and apps for electronic music artists, producers, and sound designers.",
        none: 'No products yet.',
        version: 'Version {version}'
    },

    // The showcase sections of a product's page, and of the home page's featured product.
    showcase: {
        // The button that scrolls to the demos.
        listen: 'Listen',
        // Before the operating systems the current version is for.
        runsOn: 'Runs on',
        // Before the plug-in formats, under Runs on, when the product has system requirements.
        formats: 'Formats',
        // The home page's hero image, a link to the product's page (what a screen reader says for it).
        imageLink: 'Learn more about {product}',
        gallery: 'A closer look',
        about: 'About {product}',
        // The closing panel of a product in Early Access.
        signup: {
            title: 'Be first to play {product}',
            text: "{product} is in Early Access. Leave your email address and we'll tell you the day it goes on sale.",
            email: 'Email address',
            submit: 'Notify me'
        },
        // The closing panel of a product on sale.
        own: {
            title: 'Buy it once. Keep it.',
            text: 'No subscription: a one-time purchase, with free updates until the next major version.'
        }
    },

    page: {
        allProducts: 'All products',
        betaNotice: "<strong>{product} is in Early Access.</strong> It's available only to invited Early Access Users and isn't on sale yet. Prices shown are planned and may change.",
        buy: 'Buy {product}',
        // {methods} lists paymentMethods, joined by the language's "or".
        buyTerms: '{edition} edition, one-time purchase with free updates until the next major version. Pay by {methods}. For an educational license, <ticket>open a support ticket</ticket>.',
        paymentMethods: {
            card: 'card',
            paypal: 'PayPal'
        },
        currentVersion: 'Version <strong>{version}</strong>, released {date}.',
        downloadsForBeta: 'Downloads are for Early Access Users. <link>All versions</link>',
        downloadsForLicenseHolders: 'Free to download: it runs as a demo until you activate it with a license key. <link>All versions</link>',
        notAvailable: 'Not available to download yet.',
        whatsNew: "What's new in {version}"
    },

    // A product's System requirements, on its page, as entered in the administration area.
    requirements: {
        title: 'System requirements',
        // In the box beside the description, before the platforms and formats; the link goes to the section.
        summaryLink: 'System requirements',
        minimumVersion: 'Requires',
        processors: 'Processors',
        formats: 'Formats',
        devices: 'Devices',
        // Mobile: a link to the app's store page.
        getIt: 'Get it on {store}',
        // The rows for every platform.
        allPlatforms: 'On every platform',
        processor: 'Processor',
        memory: 'Memory',
        disk: 'Disk space',
        other: 'Also needs',
        notAvailable: 'Not available',
        // Under Not available, each followed by a list joined by the language's "and".
        missingPlatforms: 'Versions for',
        missingFormats: 'Formats',
        missingArchitectures: 'Processors',
        // A platform without an architecture it could have: "Linux on ARM".
        platformOn: '{platform} on {processor}',
        platforms: {
            windows: 'Windows',
            macos: 'macOS',
            linux: 'Linux',
            ios: 'iPhone (iOS)',
            ipados: 'iPad (iPadOS)',
            android: 'Android'
        } satisfies Record<Platform, string>,
        formatNames: {
            vst3: 'VST3',
            au: 'Audio Unit (AU)',
            aax: 'AAX (Pro Tools)',
            clap: 'CLAP',
            lv2: 'LV2',
            auv3: 'AUv3',
            standalone: 'Standalone app'
        } satisfies Record<PluginFormat, string>,
        // Short, for the summary beside the description.
        formatShortNames: {
            vst3: 'VST3',
            au: 'AU',
            aax: 'AAX',
            clap: 'CLAP',
            lv2: 'LV2',
            auv3: 'AUv3',
            standalone: 'Standalone'
        } satisfies Record<PluginFormat, string>,
        // Processors: Apple's names on Apple's platforms.
        architectures: {
            x64: 'Intel/AMD (x64)',
            arm64: 'ARM (arm64)'
        } satisfies Record<Architecture, string>,
        appleArchitectures: {
            x64: 'Intel',
            arm64: 'Apple silicon'
        } satisfies Record<Architecture, string>,
        stores: {
            apple: 'the App Store',
            google: 'Google Play'
        }
    },

    gallery: {
        // For screen readers: the image is a button. Without a description, the image says nothing itself.
        viewFullSize: 'View full size',
        viewProductImageFullSize: 'View product image full size',
        previous: 'Previous image',
        next: 'Next image',
        // A thumbnail, for screen readers.
        thumbnail: 'Image {number} of {count}',
        productImage: 'Product image',
        position: '{number} of {count}',
        previousButton: 'Previous',
        nextButton: 'Next'
    },

    // /products/{sku}/download: every version's files, for license holders.
    // The audio player on a product's page.
    demos: {
        title: 'Listen',
        // Above the controls: which demo is loaded.
        trackOf: '{number} of {count}',
        play: 'Play',
        pause: 'Pause',
        previous: 'Previous demo',
        next: 'Next demo',
        back: 'Back 10 seconds',
        forward: 'Forward 10 seconds',
        seek: 'Position',
        // The position slider's value, for screen readers: "1:23 of 3:45".
        position: '{elapsed} of {total}',
        mute: 'Mute',
        unmute: 'Unmute',
        volume: 'Volume',
        list: 'Demos',
        cantPlay: "This demo can't be played in your browser."
    },

    downloads: {
        title: 'Download {product}',
        product: 'Product',
        // An Early Access product's downloads, or an Early Access Version's.
        noLicense: "These downloads of {product} are for Early Access Users, and there's no license for them on your account.",
        signInForBeta: 'These downloads of {product} are for Early Access Users: sign in with your account to download them.',
        // Instead of the list of files, for a product in Early Access.
        signInToSeeBeta: '{product} is in Early Access: its downloads are for Early Access Users. <link>Sign in</link> with your account to see them.',
        noLicenseForBeta: "{product} is in Early Access: its downloads are for Early Access Users, and there's no license for it on your account.",
        // The Early Access Versions, listed after the others for Early Access Users with an Early Access license.
        earlyAccessTitle: 'Early Access Versions',
        earlyAccessIntro: 'Builds for the Early Access Program, before their release. Activate them with your Early Access license key: your other license keys only activate the released versions.',
        earlyAccessBadge: 'Early Access Version',
        fileGone: "That file isn't available any more.",
        versionMissing: "Version {version} isn't available. The current version is shown instead.",
        fileMissing: "That file isn't available any more. Choose one below.",
        nothingYet: "There's nothing to download yet.",
        version: 'Version {version}',
        current: 'Current',
        released: 'Released {date}',
        // Before a file's checksum, for people to check their download against.
        sha256: 'SHA-256:',
        copySha256: 'Copy the SHA-256 of {file}'
    }
};

export type ProductsMessages = typeof products;
